/**
 * Demir Leather — Collection category tabs.
 *
 * Upgrades the plain links rendered by `snippets/demir-collection-tabs.liquid`
 * into an in-place filter swap, by handing the target URL to the theme's
 * existing <facets-form-component> (assets/facets.js).
 *
 * This is the same path `facet-remove-component` already takes for the
 * "remove filter" chips: `updateFiltersByURL()` pushes history state first,
 * then `sectionRenderer.renderSection()` re-fetches from
 * `window.location.href`. The form's own (now stale) checkbox state is not
 * consulted for the fetch, so handing it a URL is safe.
 *
 * Deliberately dependency-free: no `@theme/*` imports, and the handoff is
 * duck-typed rather than `instanceof`-checked. That keeps this file decoupled
 * from the theme's importmap and from facets.js load order — if the component
 * isn't there, we simply don't preventDefault and the link navigates normally.
 */
class DemirCollectionTabs extends HTMLElement {
  #listening = false;

  connectedCallback() {
    // The section is morphed in place on every filter change, which can
    // re-run this callback on a node that is already wired up.
    if (this.#listening) return;
    this.#listening = true;
    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.#listening = false;
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = /** @type {Element | null} */ (event.target);
    const link = target?.closest?.('.demir-collection-tabs__tab');
    if (!link || !this.contains(link)) return;

    // Let the browser own modified clicks (open in new tab, etc.).
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const param = this.dataset.param;
    if (!param) return;

    const facetsForm = document.querySelector('facets-form-component');
    // Duck-type: if facets.js hasn't defined/upgraded the element, fall
    // through to the href so the tab still works.
    if (typeof (/** @type {any} */ (facetsForm)?.updateFiltersByURL) !== 'function') return;

    event.preventDefault();

    // Build from the CURRENT url so unrelated facets (price, availability)
    // and the shopper's chosen `sort_by` survive a tab switch. Only this
    // tab group's param is replaced, which is what makes tabs mutually
    // exclusive rather than additive.
    const url = new URL(window.location.href);
    url.searchParams.delete(param);
    url.searchParams.delete('page');

    const value = link.getAttribute('data-value');
    if (value) url.searchParams.append(param, value);

    this.#setActive(link);

    /** @type {any} */ (facetsForm).updateFiltersByURL(url.toString());
  };

  /**
   * Move the active state optimistically so the tab responds on click rather
   * than waiting on the network. The section morph re-renders the row from
   * Liquid immediately after and confirms it.
   *
   * @param {Element} activeLink
   */
  #setActive(activeLink) {
    for (const tab of this.querySelectorAll('.demir-collection-tabs__tab')) {
      const isActive = tab === activeLink;
      tab.classList.toggle('is-active', isActive);
      if (isActive) {
        tab.setAttribute('aria-current', 'page');
      } else {
        tab.removeAttribute('aria-current');
      }
    }
  }
}

if (!customElements.get('demir-collection-tabs')) {
  customElements.define('demir-collection-tabs', DemirCollectionTabs);
}
